import { useState, useEffect, useCallback } from 'react';
import { DataTable, Column } from '../components/DataTable';
import { RichTextEditor } from '../components/RichTextEditor';
import {
  communicationService,
  EmailPayload,
  SmsPayload,
  CommunicationLogEntry,
  SmsSendResponse,
} from '../services/communications';
import { companyService, Company } from '../services/companies';
import { userService, User } from '../services/users';

type Tab = 'email' | 'sms' | 'log';
type RecipientMode = 'individual' | 'company';

export function CommunicationsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('email');

  return (
    <div>
      <div className="page-header">
        <h1>Communications</h1>
        <p>Send emails and SMS messages to portal users.</p>
      </div>

      <div className="tabs" role="tablist" aria-label="Communication tabs">
        <button
          role="tab"
          aria-selected={activeTab === 'email'}
          className={`tab ${activeTab === 'email' ? 'tab--active' : ''}`}
          onClick={() => setActiveTab('email')}
        >
          Email
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'sms'}
          className={`tab ${activeTab === 'sms' ? 'tab--active' : ''}`}
          onClick={() => setActiveTab('sms')}
        >
          SMS
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'log'}
          className={`tab ${activeTab === 'log' ? 'tab--active' : ''}`}
          onClick={() => setActiveTab('log')}
        >
          Log
        </button>
      </div>

      <div className="tab-content" role="tabpanel">
        {activeTab === 'email' && <EmailComposeTab />}
        {activeTab === 'sms' && <SmsComposeTab />}
        {activeTab === 'log' && <CommunicationLogTab />}
      </div>
    </div>
  );
}

function EmailComposeTab() {
  const [recipientMode, setRecipientMode] = useState<RecipientMode>('individual');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    async function loadData() {
      try {
        const [usersData, companiesData] = await Promise.all([
          userService.list(),
          companyService.list(),
        ]);
        setUsers(usersData);
        setCompanies(companiesData);
      } catch {
        setError('Failed to load recipients');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  async function handleSend() {
    setError(null);
    setSuccess(null);

    if (!subject.trim()) {
      setError('Subject is required');
      return;
    }
    if (!body.trim()) {
      setError('Email body is required');
      return;
    }

    const payload: EmailPayload = {
      recipients: recipientMode === 'individual' ? selectedUserIds : [],
      company_id: recipientMode === 'company' ? selectedCompanyId : undefined,
      subject: subject.trim(),
      body,
    };

    if (recipientMode === 'individual' && payload.recipients.length === 0) {
      setError('Please select at least one recipient');
      return;
    }
    if (recipientMode === 'company' && !payload.company_id) {
      setError('Please select a company');
      return;
    }

    setSending(true);
    try {
      const result = await communicationService.sendEmail(payload);
      const sentCount = result.sent_count ?? result.sent ?? 0;
      const failedCount = result.failed_count ?? result.failed ?? 0;
      const msg = `Email sent successfully to ${sentCount} recipient(s).`;
      if (failedCount > 0) {
        setSuccess(`${msg} ${failedCount} delivery failure(s).`);
      } else {
        setSuccess(msg);
      }
      setSubject('');
      setBody('');
      setSelectedUserIds([]);
      setSelectedCompanyId('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send email');
    } finally {
      setSending(false);
    }
  }

  function handleUserToggle(userId: string) {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  }

  if (loading) {
    return <div role="status">Loading...</div>;
  }

  return (
    <div className="compose-form">
      {error && <div className="alert alert-error" role="alert">{error}</div>}
      {success && <div className="alert alert-success" role="status">{success}</div>}

      <div className="form-group">
        <label>Recipient type:</label>
        <div className="radio-group">
          <label>
            <input
              type="radio"
              name="email-recipient-mode"
              value="individual"
              checked={recipientMode === 'individual'}
              onChange={() => setRecipientMode('individual')}
            />
            Individual users
          </label>
          <label>
            <input
              type="radio"
              name="email-recipient-mode"
              value="company"
              checked={recipientMode === 'company'}
              onChange={() => setRecipientMode('company')}
            />
            All users in a company
          </label>
        </div>
      </div>

      {recipientMode === 'individual' ? (
        <div className="form-group">
          <label id="email-recipients-label">Select recipients:</label>
          <div
            className="recipient-list"
            role="group"
            aria-labelledby="email-recipients-label"
          >
            {users.filter((u) => u.status === 'active').map((user) => (
              <label key={user.id} className="recipient-item">
                <input
                  type="checkbox"
                  checked={selectedUserIds.includes(user.id)}
                  onChange={() => handleUserToggle(user.id)}
                />
                {user.name} {user.last_name} ({user.email})
              </label>
            ))}
          </div>
        </div>
      ) : (
        <div className="form-group">
          <label htmlFor="email-company-select">Select company:</label>
          <select
            id="email-company-select"
            value={selectedCompanyId}
            onChange={(e) => setSelectedCompanyId(e.target.value)}
          >
            <option value="">-- Select a company --</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.usual_name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="form-group">
        <label htmlFor="email-subject">Subject:</label>
        <input
          id="email-subject"
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Enter email subject"
          className="form-control"
        />
      </div>

      <div className="form-group">
        <label id="email-body-label">Body:</label>
        <RichTextEditor
          value={body}
          onChange={setBody}
          placeholder="Compose your email..."
          aria-label="Email body"
        />
      </div>

      <button
        className="btn btn-primary"
        onClick={handleSend}
        disabled={sending}
      >
        {sending ? 'Sending...' : 'Send Email'}
      </button>
    </div>
  );
}

function SmsComposeTab() {
  const [recipientMode, setRecipientMode] = useState<RecipientMode>('individual');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [message, setMessage] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [skippedWarnings, setSkippedWarnings] = useState<SmsSendResponse['skipped']>([]);

  useEffect(() => {
    async function loadData() {
      try {
        const [usersData, companiesData] = await Promise.all([
          userService.list(),
          companyService.list(),
        ]);
        setUsers(usersData);
        setCompanies(companiesData);
      } catch {
        setError('Failed to load recipients');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  async function handleSend() {
    setError(null);
    setSuccess(null);
    setSkippedWarnings([]);

    if (!message.trim()) {
      setError('Message is required');
      return;
    }
    if (message.length > 160) {
      setError('Message exceeds 160 characters');
      return;
    }

    const payload: SmsPayload = {
      recipients: recipientMode === 'individual' ? selectedUserIds : [],
      company_id: recipientMode === 'company' ? selectedCompanyId : undefined,
      message: message.trim(),
    };

    if (recipientMode === 'individual' && payload.recipients.length === 0) {
      setError('Please select at least one recipient');
      return;
    }
    if (recipientMode === 'company' && !payload.company_id) {
      setError('Please select a company');
      return;
    }

    setSending(true);
    try {
      const result = await communicationService.sendSms(payload);
      const sentCount = result.sent_count ?? (Array.isArray(result.sent) ? result.sent.length : 0);
      const msg = `SMS sent successfully to ${sentCount} recipient(s).`;
      setSuccess(msg);

      if (result.skipped && result.skipped.length > 0) {
        setSkippedWarnings(result.skipped);
      }

      setMessage('');
      setSelectedUserIds([]);
      setSelectedCompanyId('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send SMS');
    } finally {
      setSending(false);
    }
  }

  function handleUserToggle(userId: string) {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  }

  const charCount = message.length;
  const isOverLimit = charCount > 160;

  if (loading) {
    return <div role="status">Loading...</div>;
  }

  return (
    <div className="compose-form">
      {error && <div className="alert alert-error" role="alert">{error}</div>}
      {success && <div className="alert alert-success" role="status">{success}</div>}

      {skippedWarnings.length > 0 && (
        <div className="alert alert-warning" role="alert">
          <strong>Warning:</strong> The following recipients were skipped (no valid phone number):
          <ul>
            {skippedWarnings.map((s, i) => (
              <li key={i}>{s.name} {s.last_name ?? ''} — {s.reason}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="form-group">
        <label>Recipient type:</label>
        <div className="radio-group">
          <label>
            <input
              type="radio"
              name="sms-recipient-mode"
              value="individual"
              checked={recipientMode === 'individual'}
              onChange={() => setRecipientMode('individual')}
            />
            Individual users
          </label>
          <label>
            <input
              type="radio"
              name="sms-recipient-mode"
              value="company"
              checked={recipientMode === 'company'}
              onChange={() => setRecipientMode('company')}
            />
            All users in a company
          </label>
        </div>
      </div>

      {recipientMode === 'individual' ? (
        <div className="form-group">
          <label id="sms-recipients-label">Select recipients:</label>
          <div
            className="recipient-list"
            role="group"
            aria-labelledby="sms-recipients-label"
          >
            {users.filter((u) => u.status === 'active').map((user) => (
              <label key={user.id} className="recipient-item">
                <input
                  type="checkbox"
                  checked={selectedUserIds.includes(user.id)}
                  onChange={() => handleUserToggle(user.id)}
                />
                {user.name} {user.last_name}
                {user.cell_phone ? ` (${user.cell_phone})` : ' (no phone)'}
              </label>
            ))}
          </div>
        </div>
      ) : (
        <div className="form-group">
          <label htmlFor="sms-company-select">Select company:</label>
          <select
            id="sms-company-select"
            value={selectedCompanyId}
            onChange={(e) => setSelectedCompanyId(e.target.value)}
          >
            <option value="">-- Select a company --</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.usual_name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="form-group">
        <label htmlFor="sms-message">Message:</label>
        <textarea
          id="sms-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Enter your SMS message (max 160 characters)"
          className={`form-control ${isOverLimit ? 'form-control--error' : ''}`}
          rows={3}
          maxLength={160}
        />
        <div
          className={`char-counter ${isOverLimit ? 'char-counter--over' : ''}`}
          aria-live="polite"
        >
          {charCount}/160
        </div>
      </div>

      <button
        className="btn btn-primary"
        onClick={handleSend}
        disabled={sending || isOverLimit}
      >
        {sending ? 'Sending...' : 'Send SMS'}
      </button>
    </div>
  );
}

function CommunicationLogTab() {
  const [logs, setLogs] = useState<CommunicationLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [channelFilter, setChannelFilter] = useState<'all' | 'email' | 'sms'>('all');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const pageSize = 20;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {
        status: statusFilter as 'sent' | 'delivered' | 'failed' | 'bounced' | undefined,
        page,
        page_size: pageSize,
      };

      if (channelFilter === 'all') {
        const [emailRes, smsRes] = await Promise.all([
          communicationService.getEmailLog(filters),
          communicationService.getSmsLog(filters),
        ]);
        const merged = [...emailRes.data, ...smsRes.data].sort(
          (a, b) => new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime(),
        );
        setLogs(merged);
        setTotalEntries(emailRes.total + smsRes.total);
      } else if (channelFilter === 'email') {
        const res = await communicationService.getEmailLog(filters);
        setLogs(res.data);
        setTotalEntries(res.total);
      } else {
        const res = await communicationService.getSmsLog(filters);
        setLogs(res.data);
        setTotalEntries(res.total);
      }
    } catch {
      setError('Failed to load communication logs');
    } finally {
      setLoading(false);
    }
  }, [channelFilter, statusFilter, page]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const columns: Column<CommunicationLogEntry>[] = [
    {
      key: 'channel',
      header: 'Channel',
      render: (entry) => (
        <span className={`badge badge-${entry.channel}`}>
          {entry.channel.toUpperCase()}
        </span>
      ),
      width: '80px',
    },
    {
      key: 'recipient_address',
      header: 'Recipient',
      render: (entry) => entry.recipient_name
        ? `${entry.recipient_name} (${entry.recipient_address})`
        : entry.recipient_address,
    },
    {
      key: 'subject',
      header: 'Subject/Message',
      render: (entry) => entry.subject || entry.body.substring(0, 50) + (entry.body.length > 50 ? '...' : ''),
    },
    {
      key: 'status',
      header: 'Status',
      render: (entry) => (
        <span className={`badge badge-${entry.status}`}>
          {entry.status}
        </span>
      ),
      width: '100px',
    },
    {
      key: 'sent_at',
      header: 'Sent At',
      render: (entry) => new Date(entry.sent_at).toLocaleString(),
      width: '160px',
    },
  ];

  const totalPages = Math.ceil(totalEntries / pageSize);

  return (
    <div>
      {error && <div className="alert alert-error" role="alert">{error}</div>}

      <div className="filter-bar">
        <label htmlFor="log-channel-filter">Channel:</label>
        <select
          id="log-channel-filter"
          value={channelFilter}
          onChange={(e) => { setChannelFilter(e.target.value as 'all' | 'email' | 'sms'); setPage(1); }}
        >
          <option value="all">All</option>
          <option value="email">Email</option>
          <option value="sms">SMS</option>
        </select>

        <label htmlFor="log-status-filter">Status:</label>
        <select
          id="log-status-filter"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
        >
          <option value="">All</option>
          <option value="sent">Sent</option>
          <option value="delivered">Delivered</option>
          <option value="failed">Failed</option>
          <option value="bounced">Bounced</option>
        </select>
      </div>

      <DataTable<CommunicationLogEntry>
        columns={columns}
        data={logs}
        keyField="id"
        loading={loading}
        emptyMessage="No communication logs found."
      />

      {totalPages > 1 && (
        <div className="pagination" role="navigation" aria-label="Pagination">
          <button
            className="btn btn-secondary btn-sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </button>
          <span className="pagination__info">
            Page {page} of {totalPages}
          </span>
          <button
            className="btn btn-secondary btn-sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
